import React, { useState, useEffect, useRef } from 'react';
import { Lock } from 'lucide-react';

const PASSCODE_HASH = 'b7065804da716830f45517bd6fcb311b75edabc58375df300cb584063cc0bc81';
const AUTH_KEY = 'rrdc_auth_token';
const AUTH_TIME_KEY = 'rrdc_auth_time';
const MAX_ATTEMPTS = 5;
const LOCKOUT_SECONDS = 60;
const SESSION_TIMEOUT_MS = 45 * 60 * 1000;
const WARNING_TIMEOUT_MS = 40 * 60 * 1000;

async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export function isAuthenticated() {
  const token = sessionStorage.getItem(AUTH_KEY);
  const authTime = sessionStorage.getItem(AUTH_TIME_KEY);
  if (!token || !authTime) return false;
  const elapsed = Date.now() - parseInt(authTime, 10);
  if (elapsed > SESSION_TIMEOUT_MS) {
    sessionStorage.removeItem(AUTH_KEY);
    sessionStorage.removeItem(AUTH_TIME_KEY);
    return false;
  }
  return true;
}

export function lockSession() {
  sessionStorage.removeItem(AUTH_KEY);
  sessionStorage.removeItem(AUTH_TIME_KEY);
}

export function useInactivityLock(onWarning, onLock) {
  const lastActivity = useRef(Date.now());
  const warningShown = useRef(false);

  useEffect(() => {
    const resetActivity = () => {
      lastActivity.current = Date.now();
      warningShown.current = false;
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetActivity));

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      if (elapsed >= SESSION_TIMEOUT_MS) {
        onLock();
      } else if (elapsed >= WARNING_TIMEOUT_MS && !warningShown.current) {
        warningShown.current = true;
        onWarning();
      }
    }, 10000);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      clearInterval(interval);
    };
  }, [onWarning, onLock]);
}

export default function PasscodeScreen({ onAuthenticated }) {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);
  const [checking, setChecking] = useState(false);
  const [maskedChars, setMaskedChars] = useState([]);
  const inputRef = useRef(null);
  const maskTimers = useRef([]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [locked]);

  useEffect(() => {
    if (!locked) return;
    setLockCountdown(LOCKOUT_SECONDS);
    const interval = setInterval(() => {
      setLockCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setLocked(false);
          setAttempts(0);
          setError('');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [locked]);

  // Handle passcode input with visible-then-mask effect
  const handlePasscodeChange = (e) => {
    const newValue = e.target.value;
    
    // Clear any pending mask timers for removed characters
    if (newValue.length < passcode.length) {
      maskTimers.current.slice(newValue.length).forEach(t => clearTimeout(t));
      maskTimers.current = maskTimers.current.slice(0, newValue.length);
      setMaskedChars(prev => prev.slice(0, newValue.length));
      setPasscode(newValue);
      return;
    }
    
    // New character added
    if (newValue.length > passcode.length) {
      const newCharIndex = newValue.length - 1;
      setMaskedChars(prev => [...prev, false]);
      
      const timer = setTimeout(() => {
        setMaskedChars(prev => {
          const updated = [...prev];
          if (newCharIndex < updated.length) updated[newCharIndex] = true;
          return updated;
        });
      }, 500);
      
      maskTimers.current.push(timer);
    }
    
    setPasscode(newValue);
    setError('');
  };

  const renderMaskedPasscode = () => {
    if (!passcode) return null;
    return passcode.split('').map((char, i) => (
      <span key={i} className="inline-block w-[1ch] text-center text-black">
        {maskedChars[i] ? '●' : char}
      </span>
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (locked || checking || !passcode) return;

    setChecking(true);
    const hash = await sha256(passcode);

    if (hash === PASSCODE_HASH) {
      sessionStorage.setItem(AUTH_KEY, crypto.randomUUID());
      sessionStorage.setItem(AUTH_TIME_KEY, Date.now().toString());
      onAuthenticated();
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setError('Invalid passcode');
      setPasscode('');
      setMaskedChars([]);
      maskTimers.current.forEach(t => clearTimeout(t));
      maskTimers.current = [];

      if (newAttempts >= MAX_ATTEMPTS) {
        setLocked(true);
        setError('');
      }

      await new Promise(r => setTimeout(r, 2000));
    }
    setChecking(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-b from-[#1a3a6b] to-[#2b5ea7]" data-testid="passcode-screen">
      <div className="w-full max-w-sm mx-4 text-center">
        <img
          src="/rrdc-logo.png"
          alt="Rolling River Day Camp"
          className="w-24 h-24 mx-auto mb-6 rounded-full bg-white p-1 shadow-lg"
        />
        <h1 className="text-white text-2xl font-bold mb-1">Rolling River Day Camp</h1>
        <p className="text-blue-200 text-sm mb-8">Staff Portal</p>

        {locked ? (
          <div className="bg-white/10 backdrop-blur rounded-xl p-8" data-testid="lockout-message">
            <Lock className="w-12 h-12 text-red-300 mx-auto mb-4" />
            <p className="text-white text-lg font-semibold mb-2">Too many attempts</p>
            <p className="text-blue-200 mb-4">Please wait before trying again</p>
            <div className="text-4xl font-mono text-white font-bold" data-testid="lockout-countdown">
              {lockCountdown}s
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur rounded-xl p-8">
            <div className="mb-6">
              <label className="text-blue-200 text-xs font-medium block mb-2 text-left">ENTER PASSCODE</label>
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={passcode}
                  onChange={handlePasscodeChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-transparent caret-white text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-white/50 placeholder-blue-200/50"
                  placeholder="●●●●●●●●"
                  disabled={checking}
                  autoComplete="off"
                  data-testid="passcode-input"
                />
                <div className="absolute inset-0 flex items-center px-4 pointer-events-none text-lg tracking-widest">
                  {renderMaskedPasscode()}
                </div>
              </div>
            </div>

            {error && (
              <p className="text-red-300 text-sm mb-4 font-medium" data-testid="passcode-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={checking || !passcode}
              className="w-full py-3 rounded-lg bg-white text-[#1a3a6b] font-bold text-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="passcode-submit"
            >
              {checking ? 'Verifying...' : 'Enter'}
            </button>

            {attempts > 0 && attempts < MAX_ATTEMPTS && (
              <p className="text-blue-200/70 text-xs mt-3">
                {MAX_ATTEMPTS - attempts} attempt{MAX_ATTEMPTS - attempts !== 1 ? 's' : ''} remaining
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
